<template>
  <div id="city-map" class="map-root"></div>
</template>

<script setup>
import { onMounted, watch, nextTick } from 'vue'
import L from 'leaflet'
import 'leaflet/dist/leaflet.css'

/* ================= constants ================= */
const DEFAULT_CENTER = [47.0105, 28.8638] // 🇲🇩 Moldova (Chișinău)
const DEFAULT_ZOOM = 9

/* ================= props ================= */
const props = defineProps({
  properties: {
    type: Array,
    required: true
  }
})

/* ================= state ================= */
let map = null
let markersLayer = null

/* ================= helpers ================= */
function smoothFitBounds(bounds) {
  if (!bounds.length) return

  if (bounds.length === 1) {
    map.flyTo(bounds[0], 13, {
      animate: true,
      duration: 0.9
    })
  } else {
    map.flyToBounds(bounds, {
      padding: [60, 60],
      maxZoom: 14,
      animate: true,
      duration: 1.1
    })
  }
}

/* ================= init map ================= */
function initMap() {
  if (map) return

  map = L.map('city-map', {
    zoomControl: true,
    attributionControl: false
  }).setView(DEFAULT_CENTER, DEFAULT_ZOOM)

  L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
    maxZoom: 18
  }).addTo(map)

  markersLayer = L.layerGroup().addTo(map)

  // 💥 КЛЮЧЕВОЙ ФИКС
  setTimeout(() => {
    map.invalidateSize()

    // 👉 ПЕРЕЦЕНТРОВКА
    map.setView(DEFAULT_CENTER, DEFAULT_ZOOM)
  }, 300)
}

/* ================= update markers ================= */
function updateMarkers() {
  if (!map || !markersLayer) return

  markersLayer.clearLayers()
  const bounds = []

  props.properties.forEach(p => {
    if (!p.latitude || !p.longitude) return

    const icon = L.divIcon({
      className: 'gm-pin-wrapper',
      html: `
        <div class="gm-pin">
          <div class="gm-pin-inner"></div>
        </div>
      `,
      iconSize: [32, 42],
      iconAnchor: [16, 42],
      popupAnchor: [0, -36]
    })

    const popupHtml = `
      <div class="map-popup">
        <img src="${p.cover_image_url || '/images/no-image.png'}" />
        <div class="popup-body">
          <div class="title">${p.title}</div>
          <div class="price">€${Number(p.price).toLocaleString()}</div>
          <a href="/public/properties/${p.id}" target="_blank">
            Открыть объект →
          </a>
        </div>
      </div>
    `

    const marker = L.marker([p.latitude, p.longitude], { icon })
      .bindPopup(popupHtml)

    marker.addTo(markersLayer)
    bounds.push([p.latitude, p.longitude])
  })

  if (bounds.length) {
    smoothFitBounds(bounds)
  }
}

/* ================= lifecycle ================= */
onMounted(() => {
  nextTick(() => {
    initMap()
  })
})

watch(
  () => props.properties.length,
  () => updateMarkers(),
  { immediate: true }
)
</script>

<!-- ❗️ БЕЗ scoped -->
<style>
.map-root {
  width: 100%;
  height: 100%;
}

/* ===== Google-style marker ===== */
.gm-pin-wrapper {
  background: transparent !important;
  border: none !important;
}

.gm-pin {
  position: relative;
  width: 32px;
  height: 32px;
  background: #e53935;
  border-radius: 50% 50% 50% 0;
  transform: rotate(-45deg);
  box-shadow: 0 6px 16px rgba(229, 57, 53, 0.45);
  transition: transform 0.25s ease, box-shadow 0.25s ease;
}

.gm-pin-inner {
  position: absolute;
  width: 14px;
  height: 14px;
  background: #fff;
  border-radius: 50%;
  top: 9px;
  left: 9px;
}

.leaflet-marker-icon:hover .gm-pin {
  transform: rotate(-45deg) scale(1.15);
  box-shadow: 0 10px 22px rgba(229, 57, 53, 0.65);
}

/* ===== Popup ===== */
.map-popup {
  width: 220px;
  font-family: system-ui, -apple-system, BlinkMacSystemFont;
}

.map-popup img {
  width: 100%;
  height: 120px;
  object-fit: cover;
  border-radius: 10px;
  margin-bottom: 6px;
}

.popup-body {
  font-size: 13px;
}

.popup-body .title {
  font-weight: 600;
}

.popup-body .price {
  color: #e53935;
  font-weight: 700;
  margin: 4px 0;
}

.popup-body a {
  font-size: 12px;
  color: #0d6efd;
  text-decoration: none;
}
</style>
